
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { getThemeClass } from '../../lib/theme'
import { parseAuthErrorFromHash, parseAuthErrorFromUrl, getErrorMessage, cleanupErrorUrl, AuthError } from '../../lib/auth-error-utils'
import { getStoredErrorData } from '../../lib/pre-router-error-handler'
import { useTranslation } from 'react-i18next'

export function AuthErrorPage() {
  const [authError, setAuthError] = useState<AuthError | null>(null)
  const [errorInfo, setErrorInfo] = useState<{
    title: string;
    description: string;
    isInviteError: boolean;
  } | null>(null)
  const navigate = useNavigate()
  const { t } = useTranslation()

  useEffect(() => {
    console.log('[AuthErrorPage] Checking for errors')
    
    // First, check for intercepted error data from the early interceptor
    let storedErrorString = sessionStorage.getItem('interceptedAuthError')
    let parsedError = null
    
    if (storedErrorString) {
      console.log('[AuthErrorPage] Found intercepted error data:', storedErrorString)
      parsedError = JSON.parse(storedErrorString)
      sessionStorage.removeItem('interceptedAuthError')
    } else {
      // Fallback: check for error data from pre-router handler
      parsedError = getStoredErrorData()
    }
    
    if (parsedError) {
      console.log('[AuthErrorPage] Found stored error data:', parsedError)
    } else {
      // Fallback: try to parse from current URL
      const fullUrl = window.location.href
      const hash = window.location.hash
      console.log('[AuthErrorPage] No stored error, checking URL')
      console.log('[AuthErrorPage] Full URL:', fullUrl)
      console.log('[AuthErrorPage] Hash:', hash)
      
      parsedError = parseAuthErrorFromUrl(fullUrl)
    }
    
    if (parsedError) {
      console.log('[AuthErrorPage] Successfully parsed error:', parsedError)
      setAuthError(parsedError)
      setErrorInfo(getErrorMessage(parsedError))
      // Clean up the URL to remove error parameters
      cleanupErrorUrl()
    } else {
      console.log('[AuthErrorPage] No error found, redirecting to login')
      // If no error found in URL, redirect to login
      navigate('/auth/login', { replace: true })
    }
  }, [navigate])

  const handleRetryAction = () => {
    if (errorInfo?.isInviteError) {
      // For invite errors, go to login where users can request help
      navigate('/auth/login')
    } else {
      // For other errors (like password reset), go to forgot password
      navigate('/auth/forgot-password')
    }
  }

  const handleGoToLogin = () => {
    navigate('/auth/login')
  }

  if (!authError || !errorInfo) {
    return (
      <div className={`flex min-h-screen items-center justify-center ${getThemeClass('gradients.primary')}`}>
        <div>Loading...</div>
      </div>
    )
  }

  return (
    <div className={`flex min-h-screen items-center justify-center ${getThemeClass('gradients.primary')}`}>
      <Card className={`w-full max-w-md p-6 ${getThemeClass('components.card.base')}`}>
        <div className="text-center space-y-6">
          <div className="space-y-2">
            <h1 className={`text-2xl font-bold ${getThemeClass('components.text.heading')}`}>
              {errorInfo.title}
            </h1>
          </div>

          <Alert variant="destructive" className="text-left">
            <AlertDescription>
              {errorInfo.description}
            </AlertDescription>
          </Alert>

          <div className="space-y-3">
            {errorInfo.isInviteError ? (
              <>
                <p className="text-sm text-gray-600">
                  {t('auth.error.inviteHelp', 'Contact your administrator to receive a new invitation link.')}
                </p>
                <Button 
                  onClick={handleGoToLogin}
                  className={`w-full ${getThemeClass('components.button.primary')}`}
                >
                  {t('auth.error.goToLogin', 'Go to Login')}
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={handleRetryAction}
                  className={`w-full ${getThemeClass('components.button.primary')}`}
                >
                  {authError.error_description?.includes('password') || authError.error_description?.includes('reset') 
                    ? t('auth.error.requestNewReset', 'Request New Password Reset')
                    : t('auth.error.tryAgain', 'Try Again')
                  }
                </Button>
                <Button 
                  onClick={handleGoToLogin}
                  variant="outline"
                  className="w-full"
                >
                  {t('auth.error.goToLogin', 'Go to Login')}
                </Button>
              </>
            )}
          </div>

          {/* Debug information in development */}
          {process.env.NODE_ENV === 'development' && (
            <details className="text-left text-xs text-gray-500 mt-4">
              <summary className="cursor-pointer">Debug Info</summary>
              <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto">
                {JSON.stringify(authError, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </Card>
    </div>
  )
}
